import { useMemo, useState } from "react"
import { Card } from "react-bootstrap"
import InfoTip from "@/shared/components/infoTip/InfoTip"
import type {
  MonitorSummary,
  MonitorSequenceRow,
  MonitorCounts,
} from "@services/sequence/sequenceService"
import { NO_VALUE, count, safeDivide } from "../../performanceSnapshot"

/*
    Monitor's Email & SMS tab (MPN-2208).

    THE DASHES. Open and click rates are counted from `Message.opened_at`/`clicked_at`. While
    nothing has reported one the rate is null and the cell reads as `NO_VALUE`, because a zero
    open rate on a campaign that sent ten thousand emails is a number a client will quote
    back. Those events only arrive once the SES configuration set subscribes to OPEN and CLICK
    with the matching tracking switched on (MPN-2198), so on a real campaign today the dash is
    what shows. The same dash does duty for a rate over nothing sent, which is unknown rather
    than zero -- that is `safeDivide`'s rule, shared with every other tab.
*/

const NOT_TRACKED = "Not tracked yet. Opens and clicks need tracking switched on at the provider."

/** A percentage, or the dash. `null` means unknown, which is not the same as zero. */
function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? NO_VALUE : `${value.toFixed(1)}%`
}

/** "Nov 3, 2026" -- the day the touch went out, under its name. */
function sentOn(iso: string): string {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ""
  return when.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

type Props = {
  data: MonitorSummary | null
  /** Both channels off on this campaign: the tab has nothing to report rather than zeroes. */
  channelsOn: boolean
  /**
   * False on the engagement and objective dashboards, where no one campaign is in scope.
   * Sequences are configured per campaign, so there is nothing to total across several.
   */
  hasCampaign?: boolean
  /** The request failed. A fault, not a configuration, and it has to read as one. */
  failed?: boolean
  onOpenCampaign?: () => void
}

export default function EmailSmsPerformance({
  data,
  channelsOn,
  hasCampaign = true,
  failed = false,
  onOpenCampaign,
}: Props) {
  if (failed) {
    return (
      <Card className="custom-card">
        <Card.Body className="text-center text-muted fs-12 py-4">
          Email and text results could not be loaded. Reload the page to try again.
        </Card.Body>
      </Card>
    )
  }

  if (!hasCampaign) {
    return (
      <Card className="custom-card">
        <Card.Body className="text-center text-muted fs-12 py-4">
          Choose a campaign to see its email and text results.
        </Card.Body>
      </Card>
    )
  }

  if (!channelsOn) {
    return (
      <Card className="custom-card">
        <Card.Body className="text-center py-4">
          <p className="fs-12 text-muted mb-2">
            Email and text messages aren&rsquo;t on for this campaign.
          </p>
          {onOpenCampaign && (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={onOpenCampaign}>
              Open the campaign
            </button>
          )}
        </Card.Body>
      </Card>
    )
  }

  if (!data) return null

  /* Each tile carries the count the rate came from: a percentage with no numerator is a
     figure nobody can check. The note only appears while a channel is genuinely untracked. */
  const touches = (rows: MonitorSequenceRow[]) =>
    rows.reduce((acc, row) => acc + row.touches.length, 0)

  const tiles = [
    { label: "Emails sent", value: count(data.email.sent), sub: `${touches(data.email_rows)} touches` },
    {
      label: "Email open rate",
      value: pct(data.email.open_pct),
      sub: data.email.opened ? `${count(data.email.opened)} opens` : undefined,
      note: data.email.open_pct === null ? NOT_TRACKED : undefined,
    },
    {
      label: "Email click rate",
      value: pct(data.email.click_pct),
      sub: data.email.clicked ? `${count(data.email.clicked)} clicks` : undefined,
      note: data.email.click_pct === null ? NOT_TRACKED : undefined,
    },
    { label: "Texts sent", value: count(data.sms.sent), sub: `${touches(data.sms_rows)} touches` },
    {
      label: "Text click rate",
      value: pct(data.sms.click_pct),
      sub: data.sms.clicked ? `${count(data.sms.clicked)} clicks` : undefined,
      note: data.sms.click_pct === null ? NOT_TRACKED : undefined,
    },
    {
      label: "Opt-outs",
      value: count(data.opt_outs),
      sub: `${count(data.opt_outs_by_channel.email)} email | ${count(data.opt_outs_by_channel.sms)} SMS`,
    },
  ]

  return (
    <div className="mon-esms">
      <div className="mon-esms__tiles">
        {tiles.map((tile, position) => (
          <Card className="custom-card" key={tile.label}>
            <Card.Body className="mon-esms__tile">
              <span className="mon-esms__tile-label">
                {tile.label}
                {/* The tiles sit in a row across the full width, so the last two hang their
                    bubble off the right edge or it is clipped by the viewport. */}
                {tile.note && (
                  <InfoTip
                    align={position >= tiles.length - 2 ? "right" : "left"}
                    label={tile.label}
                    text={tile.note}
                  />
                )}
              </span>
              <span className="mon-esms__tile-value">{tile.value}</span>
              {tile.sub && <span className="mon-esms__tile-sub">{tile.sub}</span>}
            </Card.Body>
          </Card>
        ))}
      </div>

      <ChannelTable
        title="Email by Sequence"
        icon="bi-envelope-at"
        iconClass="text-info"
        rows={data.email_rows}
        columns={["Sent", "Delivered %", "Open %", "Click %", "Bounce %", "Unsubscribe %"]}
        amber={data.bounce_amber}
        red={data.bounce_red}
        emptyText="No emails have been sent on this campaign yet."
        footnote="Open rates are estimates. Some email apps open messages automatically."
      />

      <ChannelTable
        title="Text Messages by Sequence"
        icon="bi-chat-dots"
        iconClass="text-success"
        rows={data.sms_rows}
        columns={["Sent", "Delivered %", "Click %", "Opt-out %", "Failed %"]}
        amber={data.bounce_amber}
        red={data.bounce_red}
        emptyText="No texts have been sent on this campaign yet."
        headNote="Texts don't report opens. Clicks come from the tracked link."
        banner={<SmsOptOutBanner data={data} />}
        isSms
      />

      <p className="fs-12 text-muted mb-0">
        Open and click rates use delivered messages. Delivered, bounce and failed rates use
        sent.
      </p>
    </div>
  )
}

/*
    The carrier band above the text table.

    This campaign's rate, and it says so. It previously carried the school's name over a
    number computed from this campaign's own sends -- a label and a value describing
    different populations, on the figure that drives "SMS pauses at 2%". The school is named
    in the tooltip, where the threshold it applies to lives.
*/
function SmsOptOutBanner({ data }: { data: MonitorSummary }) {
  const rate = data.sms.unsubscribe_pct
  if (rate === null) return null

  const tone =
    rate >= data.sms_opt_out_pause ? "bad" : rate >= data.sms_opt_out_warning ? "warn" : "ok"
  const label =
    tone === "bad"
      ? `Over ${data.sms_opt_out_pause}%`
      : tone === "warn"
        ? `Over ${data.sms_opt_out_warning}%`
        : `Under ${data.sms_opt_out_warning}%`

  return (
    <div className="mon-esms__band">
      <span className="mon-esms__band-label">Opt-out % · this campaign</span>
      <span className="mon-esms__band-value">{rate.toFixed(1)}%</span>
      <span className={`mon-esms__band-pill mon-esms__band-pill--${tone}`}>{label}</span>
      <span className="mon-esms__band-note">
        Warning at {data.sms_opt_out_warning}% · SMS pauses at {data.sms_opt_out_pause}%
        {/* The band's note is pinned to the right edge of the card, so the bubble has to
            hang off that edge or it is clipped by the viewport. */}
        <InfoTip
          align="right"
          label="SMS opt-out thresholds"
          text={`Carriers judge ${data.organization_name} on opt-out rate across every campaign. Past the pause threshold, sending should stop rather than continue.`}
        />
      </span>
    </div>
  )
}

function ChannelTable({
  title,
  icon,
  iconClass,
  rows,
  columns,
  amber,
  red,
  emptyText,
  footnote,
  headNote,
  banner,
  isSms = false,
}: {
  title: string
  icon: string
  iconClass: string
  rows: MonitorSequenceRow[]
  columns: string[]
  amber: number
  red: number
  emptyText: string
  footnote?: string
  headNote?: string
  banner?: React.ReactNode
  isSms?: boolean
}) {
  const [open, setOpen] = useState<number[]>([])
  const toggle = (id: number) =>
    setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  /* Totalled from the rows, through the same divide-or-null rule the server uses, so a
     column with nothing behind it reads as unknown rather than as zero or Infinity. */
  const total = useMemo(() => {
    const sum = (key: keyof MonitorCounts) =>
      rows.reduce((acc, row) => acc + (row[key] as number), 0)
    const sent = sum("sent")
    const delivered = sum("delivered")
    const asPct = (part: number, whole: number) => {
      const value = safeDivide(part, whole)
      return value === null ? null : value * 100
    }
    return {
      sent,
      delivered_pct: asPct(delivered, sent),
      open_pct: sum("opened") ? asPct(sum("opened"), delivered) : null,
      click_pct: sum("clicked") ? asPct(sum("clicked"), delivered) : null,
      bounce_pct: asPct(sum("bounced"), sent),
      failed_pct: asPct(sum("failed"), sent),
      unsubscribe_pct: asPct(sum("unsubscribed"), delivered),
    }
  }, [rows])

  const cells = (row: {
    sent: number
    delivered_pct: number | null
    open_pct: number | null
    click_pct: number | null
    bounce_pct: number | null
    failed_pct: number | null
    unsubscribe_pct: number | null
  }) =>
    isSms
      ? [
          count(row.sent),
          pct(row.delivered_pct),
          pct(row.click_pct),
          pct(row.unsubscribe_pct),
          pct(row.failed_pct),
        ]
      : [
          count(row.sent),
          pct(row.delivered_pct),
          pct(row.open_pct),
          pct(row.click_pct),
          pct(row.bounce_pct),
          pct(row.unsubscribe_pct),
        ]

  /** Amber over the first threshold, red over the second. Both come from the server. */
  const bounceTone = (value: number | null) => {
    if (value === null) return ""
    if (value > red) return " text-danger fw-bold"
    if (value > amber) return " text-warning fw-semibold"
    return ""
  }

  return (
    <Card className="custom-card">
      <Card.Header className="d-flex align-items-center gap-2">
        <i className={`bi ${icon} ${iconClass}`} aria-hidden="true" />
        <span className="fw-bold">{title}</span>
        {headNote && <InfoTip label={title} text={headNote} />}
        <span className="mon-esms__count">{rows.length}</span>
      </Card.Header>

      {banner}

      {rows.length === 0 ? (
        <Card.Body className="text-center text-muted fs-12 py-4">{emptyText}</Card.Body>
      ) : (
        <>
          <div className="mon-esms__scroll">
            <div
              className="mon-esms__grid"
              style={{ "--mon-esms-cols": columns.length } as React.CSSProperties}
            >
              <div className="mon-esms__row mon-esms__row--head">
                <span>Sequence</span>
                {columns.map((column) => (
                  <span key={column} className="mon-esms__num">
                    {column}
                  </span>
                ))}
              </div>

              {rows.map((row) => (
                <div key={row.id}>
                  <div className="mon-esms__row">
                    <button
                      type="button"
                      className="mon-esms__toggle"
                      aria-expanded={open.includes(row.id)}
                      onClick={() => toggle(row.id)}
                    >
                      <i
                        className={`bi ${open.includes(row.id) ? "bi-chevron-down" : "bi-chevron-right"}`}
                        aria-hidden="true"
                      />
                      <span className="mon-esms__name">{row.name}</span>
                      <span className="mon-esms__sub">
                        {row.touches.length} {row.touches.length === 1 ? "touch" : "touches"}
                      </span>
                    </button>
                    {cells(row).map((value, index) => (
                      <span
                        key={index}
                        className={`mon-esms__num${!isSms && index === 4 ? bounceTone(row.bounce_pct) : ""}`}
                      >
                        {value}
                      </span>
                    ))}
                  </div>

                  {open.includes(row.id) &&
                    row.touches.map((touch, position) => (
                      <div className="mon-esms__row mon-esms__row--touch" key={touch.id}>
                        <span className="mon-esms__name--touch">
                          <span className="mon-esms__touch-name">
                            #{position + 1} {touch.name}
                          </span>
                          {touch.sent_on && (
                            <span className="mon-esms__sent-on">Sent {sentOn(touch.sent_on)}</span>
                          )}
                        </span>
                        {cells(touch).map((value, index) => (
                          <span
                            key={index}
                            className={`mon-esms__num${!isSms && index === 4 ? bounceTone(touch.bounce_pct) : ""}`}
                          >
                            {value}
                          </span>
                        ))}
                      </div>
                    ))}
                </div>
              ))}

              <div className="mon-esms__row mon-esms__row--total">
                <span>Total</span>
                {cells(total).map((value, index) => (
                  <span key={index} className="mon-esms__num">
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {footnote && (
            <Card.Footer className="fs-12 text-muted">{footnote}</Card.Footer>
          )}
        </>
      )}
    </Card>
  )
}
